import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { AxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  baseClient,
  PortalSession,
  PortalUser,
  PortalOrganization,
  PortalSubscription,
  DirectUserSession,
  SsoSession,
  SsoConfigCheck,
} from "../api/client";
import { authApi } from "../api/auth";
import type {
  AdminSession,
  CustomerSession,
  ServiceIntegratorSession,
} from "../types";
import type { AuthRole } from "../hooks/usePermissions";

// ─── Unified Session Type ──────────────────────────────────────────────────

/**
 * Normalized session object. Every login flow produces this same shape,
 * regardless of whether the user logged in via portal email, license key,
 * SI master key, enterprise username/password, or SSO.
 */
export interface UnifiedSession {
  /** Canonical role — single source of truth for access control */
  role: AuthRole;

  /** Which token type was used to authenticate */
  tokenType: 'portal' | 'customer' | 'service-integrator' | 'user' | 'sso' | 'marketing';

  /** User identity — common fields normalized across all session types */
  user: {
    id?: number | string;
    email?: string;
    name?: string;
    avatarUrl?: string;
    username?: string;
    emailVerified?: boolean;
    hasPassword?: boolean;
    hasGoogle?: boolean;
    hasGithub?: boolean;
    language?: string;
    portalRole?: string;
  };

  /** Organization context (portal teams, enterprise customers) */
  organization?: {
    id: number;
    name?: string;
    slug?: string;
    planType?: string;
    /**
     * Real hosted tier from `organizations.tier` — 'free' | 'solo' | 'pro' |
     * 'team' | 'enterprise' (migration 129), sent by portal-auth's
     * buildSessionResponse.
     *
     * This is the ONLY field that can say "enterprise": `planType` maps the
     * legacy `organizations.plan_type` column, which is enum('individual','team')
     * and can never hold that value. Gate on `usePermissions().tier` /
     * `hasTier()`, never on `planType`.
     *
     * NULL/undefined for self-serve orgs (only the admin set-tier and
     * elevate-to-enterprise endpoints write the column) and for sessions minted
     * before the field existed — usePermissions falls back to inference then.
     */
    tier?: string | null;
    licenseKey?: string;
  } | null;

  /** Subscription state (portal users) */
  subscription?: {
    plan: string;
    status: string;
    /**
     * Flat-bundle tier pin from `subscriptions.tier` ('solo' | 'pro'), written by
     * the self-serve tier checkout. NULL on legacy à-la-carte rows. Only
     * meaningful while `status` is live — a canceled row keeps its old pin.
     */
    tier?: string | null;
    developerSeats?: number;
    stakeholderSeats?: number;
    currentPeriodEnd?: number;
    cancelAtPeriodEnd?: boolean;
    trialEndsAt?: number;
    preselectedModules?: string[];
    activeModuleIds?: string[];
  } | null;

  /**
   * Effective feature list as computed by the backend (`effectiveFeatures`),
   * identical to the `features` claim in the JWT. Lives at the top level, not
   * inside `subscription`, because an org without a `subscriptions` row still
   * carries the full free-tier capability set. Gate capabilities on this via
   * `usePermissions().hasFeature`; `subscription.activeModuleIds` only says
   * what was PURCHASED. Absent for session types that carry no feature list
   * (SSO, marketing).
   */
  activeFeatures?: string[];

  /** Membership role within organization (owner, admin, developer, stakeholder) */
  membershipRole?: string;

  /** Customer data (enterprise license key login) */
  customer?: any;

  /** Service Integrator data (SI master key login) */
  serviceIntegrator?: any;

  /** The raw JWT token (for API calls) */
  token?: string;
}

// ─── Helper ────────────────────────────────────────────────────────────────

function getAuthErrorMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { error?: string; message?: string }
      | undefined;
    return data?.error || data?.message || defaultMessage;
  }
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }
  return defaultMessage;
}

// ─── Normalizers ───────────────────────────────────────────────────────────
// Convert each legacy session type into a UnifiedSession.

/**
 * Read the hosted-tier pin off a raw session object. It IS on the wire —
 * portal-auth's `buildSessionResponse` sends `tier` on both `organization` and
 * `subscription` — but the api/auth.ts `PortalOrganization` / `PortalSubscription`
 * types do not declare it yet, so it is read structurally. Dropping it here would
 * put the tier gate straight back on `plan_type`, which can never say 'enterprise'.
 */
function readTier(source: unknown): string | null {
  if (!source || typeof source !== 'object') return null;
  const value = (source as { tier?: unknown }).tier;
  return typeof value === 'string' ? value : null;
}

function normalizePortalSession(s: PortalSession): UnifiedSession {
  const isMarketing = s.user?.portalRole === 'marketing';
  const role: AuthRole = isMarketing
    ? 'marketing'
    : s.role === 'stakeholder'
    ? 'portal_stakeholder'
    : 'portal_owner';

  return {
    role,
    tokenType: 'portal',
    user: {
      id: s.user?.id,
      email: s.user?.email,
      name: s.user?.name,
      avatarUrl: s.user?.avatarUrl,
      emailVerified: s.user?.emailVerified,
      hasPassword: s.user?.hasPassword,
      hasGoogle: s.user?.hasGoogle,
      hasGithub: s.user?.hasGithub,
      language: s.user?.language,
      portalRole: s.user?.portalRole,
    },
    organization: s.organization
      ? { ...s.organization, tier: readTier(s.organization) }
      : null,
    subscription: s.subscription
      ? { ...s.subscription, tier: readTier(s.subscription) }
      : null,
    activeFeatures: s.activeFeatures,
    membershipRole: s.role,
    token: s.token,
  };
}

function normalizeSsoSession(s: SsoSession): UnifiedSession {
  return {
    role: 'portal_owner',
    tokenType: 'sso',
    user: {
      email: (s as any).email,
      name: (s as any).displayName,
    },
    subscription: null,
    token: (s as any).token,
  };
}

function normalizeMarketingSession(s: { type: string; email: string }): UnifiedSession {
  return {
    role: 'marketing',
    tokenType: 'marketing',
    user: { email: s.email },
    subscription: null,
  };
}

// ─── Context Type ──────────────────────────────────────────────────────────

interface AuthContextType {
  // ===== UNIFIED SESSION (new — use this in new code) =====
  session: UnifiedSession | null;

  // ===== ADMIN (separate — uses httpOnly cookies, no JWT in localStorage) =====
  adminSession: AdminSession | null;
  isAdminAuthenticated: boolean;
  adminLogin: (adminKey: string) => Promise<void>;
  adminLogout: () => Promise<void>;

  // ===== SESSION STATES =====
  portalSession: PortalSession | null;
  isPortalAuthenticated: boolean;
  ssoSession: SsoSession | null;
  isSsoAuthenticated: boolean;
  marketingSession: { type: string; email: string } | null;
  isMarketingAuthenticated: boolean;


  // ===== LOGIN FUNCTIONS =====
  portalRegister: (
    email: string, password: string, name?: string,
    planType?: "individual" | "team",
    companyInfo?: { companyName?: string; billingAddress?: string; vatNumber?: string; country?: string },
    trialToken?: string,
    turnstileToken?: string | null,
  ) => Promise<{ requiresVerification: boolean; emailSent: boolean }>;
  portalLogin: (email: string, password: string, turnstileToken?: string | null) => Promise<void>;
  portalMagicLinkRequest: (email: string) => Promise<void>;
  portalMagicLinkVerify: (token: string) => Promise<void>;
  portalVerifyEmail: (token: string) => Promise<PortalSession>;
  portalOAuthGoogle: (code: string, state?: string) => Promise<void>;
  portalOAuthGithub: (code: string, state?: string) => Promise<void>;
  portalOAuthCallback: (token: string) => Promise<void>;
  portalLogout: () => Promise<void>;
  refreshPortalSession: () => Promise<void>;

  ssoCheckConfig: (customerId: number) => Promise<SsoConfigCheck>;
  ssoGetLoginUrl: (customerId: number) => string;
  ssoCallback: (token: string, email: string, displayName: string) => Promise<void>;
  ssoLogout: () => Promise<void>;

  marketingLogout: () => Promise<void>;

  // Unified logout — dispatches to the correct logout based on session type
  logout: () => Promise<void>;

  // Impersonation
  impersonating: boolean;
  startImpersonation: (token: string, session: { user: any; organization: any; subscription: any; role?: string }) => void;
  stopImpersonation: () => void;

  // Loading
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Provider ──────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  // === THE TWO REAL SESSION STATES ===
  const [session, setSession] = useState<UnifiedSession | null>(null);
  const [adminSession, setAdminSession] = useState<AdminSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // === Legacy session states (kept for backwards compat) ===
  // These are populated alongside `session` during login/mount.
  // Over time, consumers should migrate to `session.*` and these can be removed.
  const [portalSession, setPortalSession] = useState<PortalSession | null>(null);
  const [ssoSession, setSsoSession] = useState<SsoSession | null>(null);
  const [marketingSession, setMarketingSession] = useState<{ type: string; email: string } | null>(null);

  const queryClient = useQueryClient();

  // ─── Unified login helper ──────────────────────────────────────────────
  // Sets both the unified session and the appropriate legacy state.
  // Clears all other legacy states and React Query cache.

  function setPortalLogin(legacySession: PortalSession) {
    queryClient.clear();
    setSession(normalizePortalSession(legacySession));
    setPortalSession(legacySession);
    setSsoSession(null);
    setMarketingSession(null);
  }

  function setSsoLogin(legacySession: SsoSession) {
    queryClient.clear();
    setSession(normalizeSsoSession(legacySession));
    setSsoSession(legacySession);
    setPortalSession(null);
    setMarketingSession(null);
  }

  function clearSession() {
    queryClient.clear();
    setSession(null);
    setPortalSession(null);
    setSsoSession(null);
    setMarketingSession(null);
    baseClient.clearAuth();
  }

  // ─── Impersonation ────────────────────────────────────────────────────

  const [impersonating, setImpersonating] = useState(false);
  const savedAdminSessionRef = useRef<AdminSession | null>(null);
  const savedAdminTokenRef = useRef<string | null>(null);

  function startImpersonation(token: string, impersonationSession: { user: any; organization: any; subscription: any; role?: string }) {
    // Save original admin session so we can restore later
    savedAdminSessionRef.current = adminSession;

    // Build a PortalSession from the impersonation data
    const portalSess: PortalSession = {
      token,
      user: impersonationSession.user,
      organization: impersonationSession.organization,
      subscription: impersonationSession.subscription,
      role: impersonationSession.role as any,
    };

    // sessionStorage marker only — no localStorage. The marker is just a
    // "we are impersonating" flag that doesn't survive tab close/reload,
    // preventing the admin from getting stuck as the impersonated user.
    // The auth itself rides on the in-memory portal token in baseClient.
    sessionStorage.setItem('impersonation_token', token);
    apiClient.setPortalToken(token);

    // Set session state — setPortalLogin clears all other sessions
    setPortalLogin(portalSess);
    // Also clear admin session during impersonation (restored on stop)
    setAdminSession(null);
    setImpersonating(true);
  }

  function stopImpersonation() {
    // Drop the impersonation marker
    sessionStorage.removeItem('impersonation_token');

    // Clear ALL session states (unified + legacy)
    setSession(null);
    setPortalSession(null);
    setSsoSession(null);
    setMarketingSession(null);
    setImpersonating(false);

    // Clear in-memory tokens on the API client. The admin_session httpOnly
    // cookie was never touched, so admin auth resumes automatically.
    baseClient.clearAuth();

    // Restore admin session state — admin uses httpOnly cookies, so we
    // just need the React state back. The cookie is still in the browser.
    if (savedAdminSessionRef.current) {
      setAdminSession(savedAdminSessionRef.current);
      savedAdminSessionRef.current = null;
    }
    savedAdminTokenRef.current = null;

    queryClient.clear();
  }

  // ─── Mount: detect session from httpOnly cookies ──────────────────────
  //
  // Pre-migration we read JWT keys from localStorage to decide "am I logged in?"
  // Cookies are httpOnly so JS can't read them — instead we ask the backend.
  // The browser attaches whichever auth cookie is present (admin_session,
  // portal_token, marketing_session); the endpoints below resolve auth from
  // those cookies and return a 401 if absent. No localStorage involved.

  useEffect(() => { checkExistingAuth(); }, []);

  async function checkExistingAuth() {
    setIsLoading(true);
    try {
      // Clean up stale impersonation marker. If the marker is in sessionStorage,
      // the admin was impersonating and just reloaded the tab — drop the marker
      // so the admin reverts to themselves. (The in-memory portal token from
      // the impersonation flow is already gone after reload by definition.)
      if (sessionStorage.getItem("impersonation_token")) {
        console.warn("[AuthContext] Cleaning up stale impersonation marker after reload");
        sessionStorage.removeItem("impersonation_token");
      }

      // Track whether we found a primary session (local var, not stale closure).
      let foundSession = false;

      // 1. Try the portal cookie first. /me returns 200 if the cookie is valid.
      try {
        let s = await apiClient.portalGetSession();

        // Auto-switch to preferred org if it differs from the current session.
        // The preferred-org id is a user preference, not auth state — safe to
        // keep in localStorage.
        const preferredOrgId = localStorage.getItem('preferred_org_id');
        if (preferredOrgId && s.organization?.id !== parseInt(preferredOrgId)) {
          try {
            s = await apiClient.switchOrganization(parseInt(preferredOrgId));
          } catch {
            // Preferred org no longer accessible, clear preference
            localStorage.removeItem('preferred_org_id');
          }
        }

        setSession(normalizePortalSession(s));
        setPortalSession(s);
        foundSession = true;
        if (s.user?.language && (s.user.language === 'en' || s.user.language === 'nl')) {
          localStorage.setItem('language-preference', s.user.language);
          document.documentElement.setAttribute('lang', s.user.language);
        }
      } catch {
        // 401 / network error → not a portal session. Continue to other checks.
        apiClient.clearAuth();
      }

      // 2. Always check admin/marketing cookies (httpOnly, sent automatically)
      try {
        const s = await apiClient.getAdminSession();
        setAdminSession(s);
      } catch {
        setAdminSession(null);
      }

      try {
        const { data } = await baseClient.client.get("/api/auth/marketing/session");
        if (data.success && data.session) {
          // Only set session from marketing cookie if no token-based session was found.
          // Portal marketing users already have role:'marketing' via normalizePortalSession.
          if (!foundSession) {
            setSession(normalizeMarketingSession(data.session));
          }
          setMarketingSession(data.session);
        }
      } catch {
        setMarketingSession(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
    } finally {
      setIsLoading(false);
    }
  }

  // ─── Login Functions ───────────────────────────────────────────────────

  // Helper for subdomain redirect
  function redirectToSubdomainIfNeeded(slug: string, path: string) {
    const currentHost = window.location.hostname;
    const expectedHost = `${slug}.serac.build`;
    if (currentHost.includes("localhost") || currentHost === expectedHost) return;
    if (currentHost === "dashboard.serac.build" || currentHost === "serac.build") {
      window.location.href = `https://${expectedHost}${path}`;
    }
  }

  async function adminLogin(adminKey: string) {
    try {
      const s = await apiClient.adminLogin(adminKey);
      setAdminSession(s);
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "Login failed"));
    }
  }

  async function adminLogout() {
    try { await apiClient.adminLogout(); } catch {}
    setAdminSession(null);
  }

  async function portalRegister(
    email: string, password: string, name?: string,
    planType?: "individual" | "team",
    companyInfo?: { companyName?: string; billingAddress?: string; vatNumber?: string; country?: string },
    trialToken?: string,
    turnstileToken?: string | null,
  ): Promise<{ requiresVerification: boolean; emailSent: boolean }> {
    try {
      const result = await apiClient.portalRegister({
        email, password, name, plan: planType,
        ...companyInfo,
        trialToken,
        turnstileToken: turnstileToken || undefined,
        language: (localStorage.getItem('language-preference') as 'en' | 'nl') || 'en',
      });
      if (result.token && result.user) {
        setPortalLogin({
          token: result.token,
          user: result.user,
          organization: result.organization || null,
          subscription: result.subscription || null,
          activeFeatures: result.activeFeatures,
          role: result.role,
        });
      }
      return { requiresVerification: result.requiresVerification, emailSent: result.emailSent !== false };
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "Registration failed"));
    }
  }

  async function portalLogin(email: string, password: string, turnstileToken?: string | null) {
    try {
      const s = await apiClient.portalLogin(email, password, turnstileToken);
      setPortalLogin(s);
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "Login failed"));
    }
  }

  async function portalMagicLinkRequest(email: string) {
    try { await apiClient.portalMagicLinkRequest(email); }
    catch (error) { throw new Error(getAuthErrorMessage(error, "Failed to send magic link")); }
  }

  async function portalMagicLinkVerify(token: string) {
    try {
      const s = await apiClient.portalMagicLinkVerify(token);
      setPortalLogin(s);
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "Invalid or expired magic link"));
    }
  }

  async function portalVerifyEmail(token: string): Promise<PortalSession> {
    try {
      const s = await apiClient.portalVerifyEmail(token);
      setPortalLogin(s);
      return s;
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "Invalid or expired verification link"));
    }
  }

  async function portalOAuthGoogle(code: string, state?: string) {
    try {
      const s = await apiClient.portalOAuthGoogle(code, state);
      setPortalLogin(s);
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "Google login failed"));
    }
  }

  async function portalOAuthGithub(code: string, state?: string) {
    try {
      const s = await apiClient.portalOAuthGithub(code, state);
      setPortalLogin(s);
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "GitHub login failed"));
    }
  }

  async function portalOAuthCallback(token: string) {
    try {
      const s = await apiClient.portalOAuthCallback(token);
      setPortalLogin(s);
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "OAuth login failed"));
    }
  }

  async function portalLogout() {
    try { await apiClient.portalLogout(); } catch {}
    clearSession();
  }

  const refreshPortalSession = useCallback(async () => {
    try {
      const s = await apiClient.portalGetSession();
      setSession(normalizePortalSession(s));
      setPortalSession(s);
    } catch (error) {
      console.error("Failed to refresh portal session:", error);
      setPortalSession(null);
      setSession(null);
    }
  }, []);

  // ===== SSO AUTH =====

  async function ssoCheckConfig(customerId: number): Promise<SsoConfigCheck> {
    try {
      return await apiClient.ssoCheckConfig(customerId);
    } catch (error) {
      return { success: false, enabled: false, provider: null, message: getAuthErrorMessage(error, "Failed to check SSO config") };
    }
  }

  function ssoGetLoginUrl(customerId: number): string {
    return apiClient.getSsoLoginUrl(customerId);
  }

  async function ssoCallback(token: string, email: string, displayName: string) {
    try {
      // Wipe any in-memory tokens for other account types so the cross-tab
      // state is consistent. Cookies for other account types should already
      // be cleared by their respective logout/clearAuth flows.
      baseClient.clearOtherTokens('portal');

      const s = await apiClient.ssoValidateSession(token);
      setSsoLogin(s);
    } catch (error) {
      throw new Error(getAuthErrorMessage(error, "SSO authentication failed"));
    }
  }

  async function ssoLogout() {
    try { await apiClient.ssoLogout(); } catch {}
    clearSession();
  }

  // ===== MARKETING AUTH =====
  //
  // There is no marketingLogin() any more: /admin/marketing-login and its page
  // were retired with this wave (MARKETING_KEY is unset in prod, so the call
  // could only 500). The role now arrives on a normal portal session as
  // portal_users.role = 'marketing'. Logout and the marketing_session cookie
  // restore in checkAuth stay: cookies minted before this change live 7 days.

  async function marketingLogout() {
    try { await baseClient.client.post("/api/auth/marketing/logout"); } catch {}
    setMarketingSession(null);
    if (session?.tokenType === 'marketing') setSession(null);
  }

  // ─── Unified logout ────────────────────────────────────────────────────

  async function logout() {
    const tokenType = session?.tokenType;

    if (adminSession) {
      await adminLogout();
      return;
    }

    switch (tokenType) {
      case 'sso':
        await ssoLogout();
        break;
      case 'marketing':
        await marketingLogout();
        break;
      case 'customer':
      case 'service-integrator':
      case 'user':
        // Legacy session types — flows are gone, but unified logout
        // still falls back to portal logout to clear any stale state.
      case 'portal':
      default:
        await portalLogout();
        break;
    }
  }

  // ─── Context Value ─────────────────────────────────────────────────────

  const value: AuthContextType = {
    // Unified session
    session,

    // Admin (separate)
    adminSession,
    isAdminAuthenticated: !!adminSession,
    adminLogin,
    adminLogout,

    // Session states
    portalSession,
    isPortalAuthenticated: !!portalSession,
    ssoSession,
    isSsoAuthenticated: !!ssoSession,
    marketingSession,
    isMarketingAuthenticated: !!marketingSession,

    // Login functions
    portalRegister,
    portalLogin,
    portalMagicLinkRequest,
    portalMagicLinkVerify,
    portalVerifyEmail,
    portalOAuthGoogle,
    portalOAuthGithub,
    portalOAuthCallback,
    portalLogout,
    refreshPortalSession,
    ssoCheckConfig,
    ssoGetLoginUrl,
    ssoCallback,
    ssoLogout,
    marketingLogout,

    logout,

    // Impersonation
    impersonating,
    startImpersonation,
    stopImpersonation,

    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
